import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/VisibilityOutlined";
import PreviewOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { formatPersonName } from "../../../utils/formatPersonName";

const DEFAULT_REASON =
  "Conta em análise por solicitação de reembolso com atividade suspeita.";

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value;

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}T${getPart(
    "hour"
  )}:${getPart("minute")}`;
};

const formatDateTimeBR = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour12: false,
  });
};

const nowDateTimeLocal = () => toDateTimeLocalValue(new Date());

const parseRefundDueDays = (value) => {
  if (value === "" || value === null || typeof value === "undefined") return "";
  const normalized = String(value).replace(/\D/g, "");
  return normalized === "" ? "" : normalized;
};

const calculateRefundDueDate = (refundRequestedAt, refundDueDays) => {
  const days = Number(refundDueDays);
  if (!refundRequestedAt || !Number.isFinite(days) || days < 0) return null;

  const date = new Date(refundRequestedAt);
  if (Number.isNaN(date.getTime())) return null;

  date.setDate(date.getDate() + days);
  return date;
};

const AccessHoldPreview = ({ user, reason, refundRequestedAt }) => {
  const fullName = formatPersonName(user?.full_name) || "Usuário sem nome";

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        bgcolor: "rgba(12, 21, 34, 0.96)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
      }}
    >
      <Typography variant="h5" sx={{ color: "#FFF4F4", fontWeight: 800, mb: 1 }}>
        Conta temporariamente em análise
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          mb: 2.5,
          px: 1.6,
          py: 1.2,
          borderRadius: 2,
          bgcolor: "rgba(255, 193, 7, 0.12)",
          border: "1px solid rgba(255, 193, 7, 0.24)",
        }}
      >
        <WarningAmberRoundedIcon sx={{ color: "#FFD54F", fontSize: 28 }} />
        <Typography sx={{ color: "#FFE082", fontWeight: 700 }}>
          Pedido de reembolso registrado em {formatDateTimeBR(refundRequestedAt)}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: "rgba(255,255,255,0.82)",
          fontSize: "1rem",
          lineHeight: 1.7,
          mb: 3,
        }}
      >
        Seu login foi confirmado, mas o uso da ferramenta está suspenso enquanto
        avaliamos uma solicitação de reembolso com indício de atividade suspeita.
      </Typography>

      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          bgcolor: "rgba(176, 56, 56, 0.14)",
          border: "1px solid rgba(255, 132, 132, 0.18)",
        }}
      >
        <Typography sx={{ color: "#FFC6C6", fontWeight: 700, mb: 0.5 }}>
          Motivo da trava
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.84)" }}>
          {reason || DEFAULT_REASON}
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2.2,
          borderRadius: 2.5,
          bgcolor: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Typography sx={{ color: "#F6FAFF", fontWeight: 800, mb: 1.2 }}>
          Resumo da análise
        </Typography>

        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            display: "grid",
            gap: 0.9,
            color: "rgba(255,255,255,0.86)",
          }}
        >
          {[
            `Usuário: ${fullName}`,
            `E-mail: ${user?.email || "N/A"}`,
            `Pedido de reembolso: ${formatDateTimeBR(refundRequestedAt)}`,
            `Data do cadastro: ${formatDateTimeBR(user?.created_at)}`,
            `Assinatura válida até: ${formatDateTimeBR(
              user?.subscription_expires_at
            )}`,
            `Trava aplicada em: ${formatDateTimeBR(new Date())}`,
          ].map((item) => (
            <Box component="li" key={item} sx={{ lineHeight: 1.6 }}>
              {item}
            </Box>
          ))}
        </Box>
      </Box>

      <Typography
        sx={{
          mt: 3,
          color: "rgba(255,255,255,0.68)",
          lineHeight: 1.7,
        }}
      >
        Caso precise de suporte, fale com a equipe responsável pela análise.
      </Typography>
    </Box>
  );
};

const AccessHoldModal = ({ open, onClose, user, onConfirm }) => {
  const [isActive, setIsActive] = useState(false);
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [refundRequestedAt, setRefundRequestedAt] = useState("");
  const [refundDueDays, setRefundDueDays] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;

    const active = Boolean(user.access_hold_active);
    setIsActive(active);
    setReason(user.access_hold_reason || DEFAULT_REASON);
    setRefundRequestedAt(
      active
        ? toDateTimeLocalValue(
            user.access_hold_refund_requested_at || user.access_hold_created_at
          )
        : nowDateTimeLocal()
    );
    setRefundDueDays(parseRefundDueDays(user.access_hold_refund_due_days));
    setShowPreview(false);
    setError("");
  }, [user, open]);

  useEffect(() => {
    if (!isActive) {
      setShowPreview(false);
    }
  }, [isActive]);

  const handleConfirm = async () => {
    if (isSubmitting || !user?.id) return;

    if (isActive && !refundRequestedAt) {
      setError("Informe a data do pedido de reembolso.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      await onConfirm({
        is_active: isActive,
        hold_type: "suspicious_refund_review",
        reason: reason?.trim() || DEFAULT_REASON,
        refund_requested_at: isActive ? refundRequestedAt : null,
        refund_due_days: isActive && refundDueDays !== "" ? Number(refundDueDays) : null,
      });
      onClose();
    } catch (err) {
      setError(err?.message || "Erro ao salvar trava de acesso.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleAction = isActive ? "Salvar trava" : "Liberar acesso";
  const refundDueDate = calculateRefundDueDate(refundRequestedAt, refundDueDays);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "background.paper",
          color: "text.primary",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
            Trava de Reembolso
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.92rem" }}>
            {formatPersonName(user?.full_name) || "Usuário sem nome"}
          </Typography>
        </Box>

        <Tooltip title={showPreview ? "Ocultar prévia" : "Mostrar prévia"} arrow>
          <span>
            <IconButton
              onClick={() => setShowPreview((prev) => !prev)}
              disabled={!isActive}
            >
            {showPreview ? <PreviewOffIcon /> : <PreviewIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Alert severity={isActive ? "warning" : "info"}>
            {isActive
              ? "Ative a trava para bloquear o acesso enquanto o pedido de reembolso é analisado."
              : "Desative a trava para liberar novamente o acesso do usuário."}
          </Alert>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant={isActive ? "contained" : "outlined"}
              color="warning"
              onClick={() => setIsActive(true)}
            >
              Ativar trava
            </Button>
            <Button
              variant={!isActive ? "contained" : "outlined"}
              color="success"
              onClick={() => setIsActive(false)}
            >
              Liberar acesso
            </Button>
          </Box>

          <TextField
            type="datetime-local"
            label="Data do pedido de reembolso"
            value={refundRequestedAt}
            onChange={(e) => setRefundRequestedAt(e.target.value)}
            disabled={!isActive}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            type="number"
            label="Prazo do reembolso (dias)"
            value={refundDueDays}
            onChange={(e) => setRefundDueDays(parseRefundDueDays(e.target.value))}
            disabled={!isActive}
            inputProps={{ min: 0, step: 1 }}
            helperText={
              refundDueDate
                ? `Data prevista para fazer o reembolso: ${formatDateTimeBR(refundDueDate)}`
                : "Campo interno do admin. O usuário não vê essa informação."
            }
            fullWidth
          />

          <TextField
            label="Motivo da trava"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={!isActive}
            multiline
            minRows={3}
            fullWidth
          />

          {showPreview && (
            <AccessHoldPreview
              user={user}
              reason={reason}
              refundRequestedAt={refundRequestedAt}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isActive ? "warning" : "success"}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Salvando..." : titleAction}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessHoldModal;
